import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import useHighlightNewQuestions from '@/hooks/useHighlightNewQuestions';
import useToastAfterReload from '@/hooks/useToastAfterReload';
import AIQuestionBuilderPanel from './AIQuestionBuilderPanel';
import CreateWithAIButton from './CreateWithAIButton';
import ModeSelectPanel from './ModeSelectPanel';
import ReviewQuestionsPanel from './ReviewQuestionsPanel';
import type { AIQuestionBuilderProps } from './types';
import useAIQuestionBuilderStore from './useAIQuestionBuilderStore';

// AIDEV-NOTE: panels must portal to document.body to escape sidebar CSS (_question_resources.scss)
const AIQuestionBuilder = ({ assessmentID, sections, questions }: AIQuestionBuilderProps) => {
  const panelView = useAIQuestionBuilderStore(s => s.panelView);
  const rateLimited = useAIQuestionBuilderStore(s => s.rateLimited);
  const setAssessmentID = useAIQuestionBuilderStore(s => s.setAssessmentID);
  const setRateLimited = useAIQuestionBuilderStore(s => s.setRateLimited);
  const setQuestions = useAIQuestionBuilderStore(s => s.setQuestions);

  useHighlightNewQuestions();
  useToastAfterReload('ai-qb-saved', { autoClose: 3000 });

  useEffect(() => {
    setAssessmentID(assessmentID);
  }, [assessmentID, setAssessmentID]);

  useEffect(() => {
    setQuestions(questions);
  }, [questions, setQuestions]);

  useEffect(() => {
    axios.get(Routes.plato_api_ai_rate_limit_path())
      .then((response) => {
        if (response.data.rate_limited) {
          setRateLimited(true);
        }
      })
      .catch(() => {
      });
  }, [setRateLimited]);

  const isOpen = panelView !== 'closed';
  const closePanel = useAIQuestionBuilderStore(s => s.closePanel);

  return (
    <>
      <CreateWithAIButton rateLimited={rateLimited} />

      {isOpen && createPortal(
        <div
          className="tw-fixed tw-inset-0 tw-bg-black/40 tw-z-[1040]"
          role="presentation"
          onClick={(panelView === 'mode_select' || panelView === 'configure') ? closePanel : undefined}
        />,
        document.body,
      )}

      {panelView === 'mode_select' && createPortal(
        <ModeSelectPanel />,
        document.body,
      )}

      {panelView === 'configure' && createPortal(
        <AIQuestionBuilderPanel sections={sections} />,
        document.body,
      )}

      {panelView === 'review' && createPortal(
        <ReviewQuestionsPanel />,
        document.body,
      )}
    </>
  );
};

export default AIQuestionBuilder;
